import { createContext, useContext, useEffect, useId } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { createPortal } from "react-dom";
import useNavigate from "core/hooks/useNavigate";
import Typography from "../Typography";

const PanelContainer = styled.div`
  box-sizing: border-box;
  position: fixed;
  top: 0px;
  right: 0px;
  height: 100vh;
  scrollbar-gutter: stable;
  padding: 50px 25px;
  padding-right: env(10px, safe-area-inset-right);
  overflow: auto;
  scrollbar-width: thin;
  z-index: 10000;
  border: 1px solid #898d8f;
  border-radius: 5px 0 0 5px;
  background: #fff;
  box-shadow: -4px 0 8px 0 rgba(0, 0, 0, 0.15);
  width: 464px;
  max-width: 100vw;
`;

// Bare button reset so the close control is a real, focusable <button> while
// keeping the exact look/position the icon had before.
const PanelCloseButton = styled.button`
  position: absolute;
  top: 22px;
  right: 22px;
  padding: 0;
  border: none;
  background: none;
  line-height: 0;
  cursor: pointer;
  color: #000;
`;

// Lets PanelTitle pick up the id the Panel points its aria-labelledby at,
// without callers having to thread an id through themselves.
const PanelContext = createContext(null);

export const Panel = ({
  isOpen,
  onOpenChange,
  closeOnOutsideClick,
  onCloseRedirectTo,
  ariaLabel,
  children,
}) => {
  const navigate = useNavigate();
  const titleId = useId();

  useEffect(() => {
    const handleBodyClick = (event) => {
      const hasClickedTrigger = event.target.closest(".panel__trigger");
      const hasClickedOutside = !event.target.closest(".panel");

      if (hasClickedOutside && !hasClickedTrigger && closeOnOutsideClick) {
        onOpenChange && onOpenChange(false);
      }
    };

    if (isOpen) {
      document.body.addEventListener("click", handleBodyClick);
      return () => document.body.removeEventListener("click", handleBodyClick);
    } else {
      document.body.removeEventListener("click", handleBodyClick);
    }
  }, [isOpen, onOpenChange, onCloseRedirectTo, closeOnOutsideClick, navigate]);

  return createPortal(
    <PanelContainer
      className="panel"
      role="dialog"
      // Not aria-modal: the panel does not trap focus and the page behind it
      // stays interactive, so claiming modality would mislead screen readers.
      aria-label={ariaLabel}
      // An explicit ariaLabel wins; otherwise the rendered PanelTitle names it.
      aria-labelledby={ariaLabel ? undefined : titleId}
      as={motion.div}
      initial={{ right: "-465px" }}
      animate={{ right: isOpen ? "1px" : "-465px" }}
      exit={{ right: "-465px" }}
      transition={{
        duration: 0.35,
        ease: "easeInOut",
      }}
      onAnimationComplete={() => {
        if (!isOpen && onCloseRedirectTo) {
          navigate(onCloseRedirectTo);
        }
      }}
    >
      <PanelCloseButton
        type="button"
        aria-label="Close panel"
        onClick={() => {
          onOpenChange && onOpenChange(false);
        }}
      >
        <FontAwesomeIcon icon={faClose} size="lg" />
      </PanelCloseButton>

      <PanelContext.Provider value={{ titleId }}>
        {children}
      </PanelContext.Provider>
    </PanelContainer>,
    document.body
  );
};

export const PanelHeader = styled.div`
  min-width: 0px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

export const PanelTitle = ({ children }) => {
  // Outside a Panel (e.g. isolated stories) there is no id to claim.
  const panel = useContext(PanelContext);

  return (
    <Typography variant="h3" noMargin id={panel?.titleId}>
      {children}
    </Typography>
  );
};

export const PanelDescription = ({ children }) => {
  return (
    <Typography variant="body" noMargin>
      {children}
    </Typography>
  );
};

export const PanelContent = styled.div``;
